/**
 * CosyflowPicker - Modular Cosyflow Selector Component
 * Converted from TypeScript for use in PromptusAI PWA
 *
 * This component has no external context dependencies - all functionality is passed via props.
 *
 * MERGED VERSION: Combines localStorage state persistence from current repo with
 * fetchUserCosyflows and initialShowOnlyMine props from new repo.
 */

import React, { useState, useEffect, useMemo, useCallback } from 'react';
import axios from 'axios';
import { CosyflowCard } from './CosyflowCard';
import { getUnifiedOfflineModels, checkOfflineServersAvailable, refreshModelCache } from '../../services/offlineModelService';
import { db } from '../../db';

// ============================================
// Default Components
// ============================================

const DefaultLoadingSpinner = () => (
  <div className="fixed inset-0 flex items-center justify-center bg-dark-blue z-50">
    <div className="flex flex-col items-center justify-center gap-3">
      <div className="w-12 h-12 border-4 border-white/20 border-t-yellow rounded-full animate-spin" />
      <p className="text-base text-white/60 animate-pulse">Loading cosyflows...</p>
    </div>
  </div>
);

const SearchIcon = () => (
  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
  </svg>
);

const BackIcon = () => (
  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
  </svg>
);

const EmptyIcon = () => (
  <svg className="w-16 h-16" fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9.172 16.172a4 4 0 015.656 0M9 10h.01M15 10h.01M12 12h.01M12 14h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
  </svg>
);

const SortIcon = () => (
  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 4h13M3 8h9m-9 4h6m4 0l4-4m0 0l4 4m-4-4v12" />
  </svg>
);

const StarIcon = () => (
  <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 24 24">
    <path d="M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z" />
  </svg>
);

const FireIcon = () => (
  <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 24 24">
    <path d="M12 23c-4.97 0-9-3.58-9-8 0-2.52 1.17-5.43 3.5-8.73.4-.57 1.14-.72 1.71-.32.21.15.37.36.46.6l.63 1.77c.22.61.75 1.04 1.39 1.12.64.08 1.27-.22 1.64-.77l2.67-4c.33-.5.95-.7 1.51-.5.56.2.93.72.93 1.31V9c0 1.1.9 2 2 2s2-.9 2-2V5.64c0-.55.22-1.08.62-1.46.4-.38.94-.58 1.5-.54.55.04 1.06.31 1.38.76C20.36 6.91 21 9.91 21 12c0 6.08-4.03 11-9 11z" />
  </svg>
);

const GridIcon = () => (
  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2H6a2 2 0 01-2-2V6zM14 6a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2h-2a2 2 0 01-2-2V6zM4 16a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2H6a2 2 0 01-2-2v-2zM14 16a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2h-2a2 2 0 01-2-2v-2z" />
  </svg>
);

// Sort options configuration (renamed from All to Alphabetical for clarity)
const SORT_OPTIONS = [
  { value: 'recommended', label: 'Recommended' },
  { value: 'newest', label: 'Newest' },
  { value: 'cost', label: 'Cost' },
  { value: 'alphabetical', label: 'A-Z' },
];

// Generation type options
const GENERATION_TYPE_OPTIONS = [
  { value: 'all', label: 'Outputs' },
  { value: 'image', label: 'Image' },
  { value: 'video', label: 'Video' },
  { value: 'audio', label: 'Audio' },
  { value: '3d', label: '3D' },
];

// Property options for tri-state filtering
const PROPERTY_OPTIONS = [
  { value: 'availableOnline', label: 'Available Online' },
  { value: 'availableLocally', label: 'Available Locally' },
  { value: 'mine', label: 'Mine' },
  { value: 'hasWorkflow', label: 'Has Workflow' },
  { value: 'trusted', label: 'Trusted' },
  { value: 'public', label: 'Public' },
  { value: 'distributed', label: 'Distributed' },
];

// Arrow icons for cost toggle
const ArrowUpIcon = () => (
  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 15l7-7 7 7" />
  </svg>
);

const ArrowDownIcon = () => (
  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
  </svg>
);

const ChevronDownIcon = () => (
  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
  </svg>
);

const PlusIcon = () => (
  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
  </svg>
);

const MinusIcon = () => (
  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M20 12H4" />
  </svg>
);

const FilterIcon = () => (
  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 4a1 1 0 011-1h16a1 1 0 011 1v2.586a1 1 0 01-.293.707l-6.414 6.414a1 1 0 00-.293.707V17l-4 4v-6.586a1 1 0 00-.293-.707L3.293 7.293A1 1 0 013 6.586V4z" />
  </svg>
);

const CheckIcon = () => (
  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
  </svg>
);

// ============================================
// Multi-Select Use Case Dropdown Component with Badge
// ============================================

const MultiSelectUseCaseDropdown = ({ selectedUseCases = [], onChange, useCases, counts = {} }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const dropdownRef = React.useRef(null);
  const searchInputRef = React.useRef(null);

  // Filter tasks based on search
  const filteredUseCases = useMemo(() => {
    if (!searchQuery) return useCases;
    const query = searchQuery.toLowerCase();
    return useCases.filter(uc => uc.toLowerCase().includes(query));
  }, [useCases, searchQuery]);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
        setSearchQuery('');
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Focus search input when dropdown opens
  useEffect(() => {
    if (isOpen && searchInputRef.current) {
      searchInputRef.current.focus();
    }
  }, [isOpen]);

  const handleToggle = (useCase) => {
    if (selectedUseCases.includes(useCase)) {
      onChange(selectedUseCases.filter(uc => uc !== useCase));
    } else {
      onChange([...selectedUseCases, useCase]);
    }
  };

  const handleClearAll = () => {
    onChange([]);
    setIsOpen(false);
    setSearchQuery('');
  };

  const displayValue = selectedUseCases.length === 0
    ? 'Tasks'
    : selectedUseCases.length === 1
      ? selectedUseCases[0]
      : `${selectedUseCases.length} Tasks`;

  return (
    <div className="relative" ref={dropdownRef}>
      {/* Dropdown Button with Badge */}
      <button
        onClick={() => setIsOpen(!isOpen)}
        className={`flex items-center justify-between gap-2 bg-dark-blue/30 border rounded-lg px-4 py-3 text-white text-base focus:outline-none focus:ring-2 focus:ring-cyan/50 cursor-pointer min-w-[140px] hover:border-white/40 transition-colors ${
          selectedUseCases.length > 0 ? 'border-cyan/50' : 'border-white/20'
        }`}
      >
        <span className="truncate">{displayValue}</span>
        <div className="flex items-center gap-1">
          {selectedUseCases.length > 0 && (
            <span className="bg-cyan text-dark-blue text-xs font-bold px-1.5 py-0.5 rounded-full min-w-[20px] text-center">
              {selectedUseCases.length}
            </span>
          )}
          <ChevronDownIcon />
        </div>
      </button>

      {/* Dropdown Panel */}
      {isOpen && (
        <div className="absolute top-full left-0 mt-1 w-72 bg-dark-blue border border-white/20 rounded-lg shadow-xl z-[100] overflow-hidden">
          {/* Search Input */}
          <div className="p-2 border-b border-white/10">
            <div className="relative">
              <div className="absolute left-3 top-1/2 -translate-y-1/2 text-white/40">
                <SearchIcon />
              </div>
              <input
                ref={searchInputRef}
                type="text"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                placeholder="Search tasks..."
                className="w-full pl-10 pr-4 py-2.5 bg-white/5 border border-white/10 rounded-lg text-white placeholder-white/40 focus:outline-none focus:ring-1 focus:ring-cyan/50 text-base"
              />
            </div>
          </div>

          {/* Clear All Option */}
          {selectedUseCases.length > 0 && (
            <>
              <button
                onClick={handleClearAll}
                className="w-full flex items-center justify-between px-4 py-3 text-left text-base transition-colors text-white/60 hover:bg-white/10"
              >
                <span>Clear All ({selectedUseCases.length} selected)</span>
              </button>
              <div className="border-t border-white/10" />
            </>
          )}

          {/* Options List */}
          <div className="max-h-64 overflow-y-auto">
            {filteredUseCases.length > 0 ? (
              filteredUseCases.map((useCase) => {
                const isSelected = selectedUseCases.includes(useCase);
                const count = counts[useCase] || 0;
                return (
                  <button
                    key={useCase}
                    onClick={() => handleToggle(useCase)}
                    className={`w-full flex items-center justify-between px-4 py-3 text-left text-base transition-colors ${
                      isSelected
                        ? 'bg-cyan/20 text-cyan'
                        : 'text-white hover:bg-white/10'
                    }`}
                  >
                    <span>{useCase} ({count})</span>
                    {isSelected && <CheckIcon />}
                  </button>
                );
              })
            ) : (
              <div className="px-4 py-3 text-white/50 text-base text-center">
                No tasks found
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

// ============================================
// Multi-Select Users Dropdown Component with Badge
// ============================================

const MultiSelectUsersDropdown = ({ selectedUsers = [], onChange, users, counts = {} }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const dropdownRef = React.useRef(null);
  const searchInputRef = React.useRef(null);

  // Filter users based on search
  const filteredUsers = useMemo(() => {
    if (!searchQuery) return users;
    const query = searchQuery.toLowerCase();
    return users.filter(user => user.toLowerCase().includes(query));
  }, [users, searchQuery]);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
        setSearchQuery('');
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Focus search input when dropdown opens
  useEffect(() => {
    if (isOpen && searchInputRef.current) {
      searchInputRef.current.focus();
    }
  }, [isOpen]);

  const handleToggle = (user) => {
    if (selectedUsers.includes(user)) {
      onChange(selectedUsers.filter(u => u !== user));
    } else {
      onChange([...selectedUsers, user]);
    }
  };

  const handleClearAll = () => {
    onChange([]);
    setIsOpen(false);
    setSearchQuery('');
  };

  const displayValue = selectedUsers.length === 0
    ? 'Users'
    : selectedUsers.length === 1
      ? selectedUsers[0]
      : `${selectedUsers.length} Users`;

  return (
    <div className="relative" ref={dropdownRef}>
      {/* Dropdown Button with Badge */}
      <button
        onClick={() => setIsOpen(!isOpen)}
        className={`flex items-center justify-between gap-2 bg-dark-blue/30 border rounded-lg px-4 py-3 text-white text-base focus:outline-none focus:ring-2 focus:ring-cyan/50 cursor-pointer min-w-[140px] hover:border-white/40 transition-colors ${
          selectedUsers.length > 0 ? 'border-cyan/50' : 'border-white/20'
        }`}
      >
        <span className="truncate">{displayValue}</span>
        <div className="flex items-center gap-1">
          {selectedUsers.length > 0 && (
            <span className="bg-cyan text-dark-blue text-xs font-bold px-1.5 py-0.5 rounded-full min-w-[20px] text-center">
              {selectedUsers.length}
            </span>
          )}
          <ChevronDownIcon />
        </div>
      </button>

      {/* Dropdown Panel */}
      {isOpen && (
        <div className="absolute top-full left-0 mt-1 w-72 bg-dark-blue border border-white/20 rounded-lg shadow-xl z-50 overflow-hidden">
          {/* Search Input */}
          <div className="p-2 border-b border-white/10">
            <div className="relative">
              <div className="absolute left-3 top-1/2 -translate-y-1/2 text-white/40">
                <SearchIcon />
              </div>
              <input
                ref={searchInputRef}
                type="text"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                placeholder="Search users..."
                className="w-full pl-10 pr-4 py-2.5 bg-white/5 border border-white/10 rounded-lg text-white placeholder-white/40 focus:outline-none focus:ring-1 focus:ring-cyan/50 text-base"
              />
            </div>
          </div>

          {/* Clear All Option */}
          {selectedUsers.length > 0 && (
            <>
              <button
                onClick={handleClearAll}
                className="w-full flex items-center justify-between px-4 py-3 text-left text-base transition-colors text-white/60 hover:bg-white/10"
              >
                <span>Clear All ({selectedUsers.length} selected)</span>
              </button>
              <div className="border-t border-white/10" />
            </>
          )}

          {/* Options List */}
          <div className="max-h-64 overflow-y-auto">
            {filteredUsers.length > 0 ? (
              filteredUsers.map((user) => {
                const isSelected = selectedUsers.includes(user);
                const count = counts[user] || 0;
                return (
                  <button
                    key={user}
                    onClick={() => handleToggle(user)}
                    className={`w-full flex items-center justify-between px-4 py-3 text-left text-base transition-colors ${
                      isSelected
                        ? 'bg-cyan/20 text-cyan'
                        : 'text-white hover:bg-white/10'
                    }`}
                  >
                    <span>{user} ({count})</span>
                    {isSelected && <CheckIcon />}
                  </button>
                );
              })
            ) : (
              <div className="px-4 py-3 text-white/50 text-base text-center">
                No users found
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

// ============================================
// Properties Dropdown Component (Tri-State)
// ============================================

const XIcon = () => (
  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
  </svg>
);

const DashIcon = () => (
  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M20 12H4" />
  </svg>
);

const PropertiesDropdown = ({ properties = {}, onChange, counts = {} }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [openMenu, setOpenMenu] = useState(null); // which property's sub-menu is open
  const dropdownRef = React.useRef(null);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
        setOpenMenu(null);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const activeCount = Object.keys(properties).length;

  const handleSet = (key, value) => {
    const updated = { ...properties };
    if (value === null) {
      delete updated[key];
    } else {
      updated[key] = value;
    }
    onChange(updated);
    setOpenMenu(null);
  };

  const handleClearAll = () => {
    onChange({});
    setIsOpen(false);
    setOpenMenu(null);
  };

  return (
    <div className="relative" ref={dropdownRef}>
      {/* Dropdown Button with Badge */}
      <button
        onClick={() => { setIsOpen(!isOpen); setOpenMenu(null); }}
        className={`flex items-center justify-between gap-2 bg-dark-blue/30 border rounded-lg px-4 py-3 text-white text-base focus:outline-none focus:ring-2 focus:ring-cyan/50 cursor-pointer min-w-[140px] hover:border-white/40 transition-colors ${
          activeCount > 0 ? 'border-cyan/50' : 'border-white/20'
        }`}
      >
        <span className="truncate">Properties</span>
        <div className="flex items-center gap-1">
          {activeCount > 0 && (
            <span className="bg-cyan text-dark-blue text-xs font-bold px-1.5 py-0.5 rounded-full min-w-[20px] text-center">
              {activeCount}
            </span>
          )}
          <ChevronDownIcon />
        </div>
      </button>

      {/* Dropdown Panel */}
      {isOpen && (
        <div className="absolute top-full left-0 mt-1 w-64 bg-dark-blue border border-white/20 rounded-lg shadow-xl z-[100]">
          {/* Clear All Option */}
          {activeCount > 0 && (
            <>
              <button
                onClick={handleClearAll}
                className="w-full flex items-center justify-between px-4 py-3 text-left text-base transition-colors text-white/60 hover:bg-white/10 rounded-t-lg"
              >
                <span>Clear All ({activeCount} active)</span>
              </button>
              <div className="border-t border-white/10" />
            </>
          )}

          {/* Options List */}
          <div className="py-1">
            {PROPERTY_OPTIONS.map((option) => {
              const state = properties[option.value]; // undefined | 'true' | 'false'
              const countData = counts[option.value] || { yes: 0, no: 0 };
              const yesCount = typeof countData === 'object' ? countData.yes : countData;
              const noCount = typeof countData === 'object' ? countData.no : 0;
              const totalCount = yesCount + noCount;
              const isSubOpen = openMenu === option.value;
              return (
                <div key={option.value} className="relative">
                  <button
                    onClick={() => setOpenMenu(isSubOpen ? null : option.value)}
                    className={`w-full flex items-center px-4 py-3 text-left text-base transition-colors gap-2 ${
                      isSubOpen ? 'bg-white/10' : 'hover:bg-white/5'
                    }`}
                  >
                    <span className={`flex items-center px-1.5 py-0.5 rounded ${
                      state === 'true'
                        ? 'bg-cyan/30 text-cyan'
                        : state === 'false'
                        ? 'bg-red-500/30 text-red-400'
                        : 'text-white/30'
                    }`}>
                      {state === 'true' ? <CheckIcon /> : state === 'false' ? <XIcon /> : <DashIcon />}
                    </span>
                    <span className={`flex-1 ${state === 'true' ? 'text-cyan' : state === 'false' ? 'text-red-400' : 'text-white'}`}>
                      {option.label}
                    </span>
                    <span className="text-white/30">
                      <ChevronDownIcon />
                    </span>
                  </button>

                  {/* Sub-menu */}
                  {isSubOpen && (
                    <div className="flex items-center gap-1 px-6 py-2 bg-white/5 border-t border-b border-white/5">
                      <button
                        onClick={() => handleSet(option.value, null)}
                        className={`flex items-center gap-1 px-2 py-1 rounded text-sm transition-colors ${
                          !state ? 'text-white bg-white/15' : 'text-white/50 hover:bg-white/10'
                        }`}
                      >
                        <DashIcon /> ({totalCount})
                      </button>
                      <button
                        onClick={() => handleSet(option.value, 'true')}
                        className={`flex items-center gap-1 px-2 py-1 rounded text-sm transition-colors ${
                          state === 'true' ? 'text-cyan bg-cyan/20' : 'text-white/50 hover:bg-white/10'
                        }`}
                      >
                        <CheckIcon /> ({yesCount})
                      </button>
                      <button
                        onClick={() => handleSet(option.value, 'false')}
                        className={`flex items-center gap-1 px-2 py-1 rounded text-sm transition-colors ${
                          state === 'false' ? 'text-red-400 bg-red-500/20' : 'text-white/50 hover:bg-white/10'
                        }`}
                      >
                        <XIcon /> ({noCount})
                      </button>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
};

// ============================================
// CosyflowPicker Component
// ============================================

const DEFAULT_CLASS_NAMES = {};
const DEFAULT_CARD_CLASS_NAMES = {};
const DEFAULT_STYLES = {};
const DEFAULT_GRID_COLUMNS = { sm: 1, md: 2, lg: 3, xl: 4 };

export function CosyflowPicker({
  // Data
  cosyflows: externalCosyflows,
  fetchCosyflows,
  fetchUserCosyflows,  // NEW: Fetch user's own cosyflows
  fetchAvailableModels,  // Fetch available model names from API
  isLoading: externalLoading,
  refreshKey,  // Change this value to trigger a refetch

  // Callbacks
  onSelect,
  onBack,
  onRunInPlayground,
  onRunOfflineInPlayground,
  onRunInCosyUI,
  onRunInComfyUI,
  onOpenEditor,
  onCreateTemplate,
  onInstallLocally,
  isInstalling,
  onModifyCosyflow,
  onDeleteCosyflow,
  onViewDetails,
  onCreatorClick,
  onMakeAvailableLocally,
  onMakeAvailableOnline,
  onCreateFromDistributed,
  onTogglePublic,
  onToggleDistributed,

  // User Context
  currentUserAccountNo,
  currentUserAlias,
  isStaffAccount = false,
  isStaffOrCreatorAccount = false,
  showOwnershipFilter,
  initialShowOnlyMine = false,  // NEW: Initial state for ownership filter

  // Pricing
  getCostDisplay,

  // Available models for "Run in Playground" button visibility (initial value)
  availableModels: initialAvailableModels = [],

  // Local available models (from Local ComfyUI API)
  localAvailableModels: initialLocalAvailableModels = [],

  // Trust filtering - when false, only show trusted cosyflows (except user's own and local)
  allowUntrusted = false,

  // Content
  title = 'Select a Cosyflow',
  subtitle = 'Browse and select a cosyflow to use',
  backText = 'Back',
  searchPlaceholder = 'Search cosyflows...',
  emptyTitle = 'No cosyflows found',
  emptyMessage = 'Try adjusting your search query.',
  emptyOwnedMessage = "You haven't created any cosyflows yet.",
  ctaText = 'Use This Cosyflow',

  // Styling
  classNames = DEFAULT_CLASS_NAMES,
  cardClassNames = DEFAULT_CARD_CLASS_NAMES,
  styles = DEFAULT_STYLES,
  outputTypeConfig,

  // Customization
  LoadingComponent,
  EmptyComponent,
  renderCard,
  renderImage,
  getPlaceholderImage,
  showActionButtons = true,

  // Layout
  showHeader = true,
  gridColumns = DEFAULT_GRID_COLUMNS,
}) {
  // ============================================
  // State Persistence Key (from current repo)
  // ============================================
  const STORAGE_KEY = 'cosyflowPicker_state';
  const storageKey = useMemo(() => (
    currentUserAccountNo ? `${STORAGE_KEY}_${currentUserAccountNo}` : STORAGE_KEY
  ), [currentUserAccountNo]);
  const defaultFilters = useMemo(() => ({
    minSpeed: 0,
    minQuality: 0,
    maxCost: 100,
    category: 'all',
    useCase: 'all',
    useCases: [], // Multi-select use cases
    users: [], // Multi-select users/creators
    generationType: 'all', // image, video, audio, 3d
    properties: {}, // { [key]: 'true' | 'false' } - tri-state property filters
  }), []);
  const normalizeSortBy = useCallback((value) => {
    if (value === 'all') return 'recommended';
    if (value === 'popularity') return 'popular';
    return SORT_OPTIONS.some(option => option.value === value) ? value : 'recommended';
  }, []);

  // Load persisted state from localStorage
  const getPersistedState = useCallback(() => {
    try {
      const saved = localStorage.getItem(storageKey);
      if (saved) {
        return JSON.parse(saved);
      }
    } catch (e) {
      console.warn('[CosyflowPicker] Failed to load persisted state:', e);
    }
    return null;
  }, [storageKey]);

  const persistedState = useMemo(() => getPersistedState(), [getPersistedState]);
  const initialSortBy = normalizeSortBy(persistedState?.sortBy);
  const initialFilters = useMemo(() => {
    if (!persistedState?.filters) return defaultFilters;
    const saved = { ...defaultFilters, ...persistedState.filters };
    // Migrate old runsOn/trustFlags to properties format
    if (saved.runsOn || saved.trustFlags) {
      const migrated = { ...(saved.properties || {}) };
      if (saved.runsOn === 'online') migrated.availableOnline = 'true';
      if (saved.runsOn === 'local') migrated.availableLocally = 'true';
      if (Array.isArray(saved.trustFlags)) {
        saved.trustFlags.forEach(flag => {
          migrated[flag] = 'true';
        });
      }
      saved.properties = migrated;
      delete saved.runsOn;
      delete saved.trustFlags;
    }
    return saved;
  }, [persistedState, defaultFilters]);

  // ============================================
  // State (merged: uses persistence + initialShowOnlyMine)
  // ============================================
  const [cosyflows, setCosyflows] = useState(externalCosyflows || []);
  const [isLoading, setIsLoading] = useState(externalLoading ?? !externalCosyflows);
  const [searchQuery, setSearchQuery] = useState(persistedState?.searchQuery || '');
  // Use persisted showOnlyMine if available, otherwise fall back to initialShowOnlyMine
  const [showOnlyMine, setShowOnlyMine] = useState(persistedState?.showOnlyMine ?? initialShowOnlyMine);
  const [showOnlyAvailable, setShowOnlyAvailable] = useState(false);
  const [showOnlyOnline, setShowOnlyOnline] = useState(false);
  const [showOnlyLocal, setShowOnlyLocal] = useState(false);
  const [availableModels, setAvailableModels] = useState(initialAvailableModels);
  const [offlineModels, setOfflineModels] = useState([]);
  const [hasLocalServer, setHasLocalServer] = useState(false);
  const [localCosyflows, setLocalCosyflows] = useState([]);
  const [localAvailableModels, setLocalAvailableModels] = useState(initialLocalAvailableModels);
  const [sortBy, setSortBy] = useState(initialSortBy);
  const [costSortAsc, setCostSortAsc] = useState(persistedState?.costSortAsc ?? true); // true = low to high, false = high to low
  const [showFilters, setShowFilters] = useState(persistedState?.showFilters || false);
  const [filters, setFilters] = useState(initialFilters);
  const [defaultModels, setDefaultModels] = useState([]);

  // Persist state to localStorage whenever it changes (from current repo)
  useEffect(() => {
    try {
      const stateToSave = {
        searchQuery,
        showOnlyMine,
        sortBy,
        costSortAsc,
        showFilters,
        filters,
      };
      localStorage.setItem(storageKey, JSON.stringify(stateToSave));
    } catch (e) {
      console.warn('[CosyflowPicker] Failed to persist state:', e);
    }
  }, [searchQuery, showOnlyMine, sortBy, costSortAsc, showFilters, filters, storageKey]);

  // Sync availableModels when prop changes (fixes timing issue with "Available Online" filter)
  useEffect(() => {
    if (initialAvailableModels && initialAvailableModels.length > 0) {
      console.log('[CosyflowPicker] Syncing availableModels from props:', initialAvailableModels.length);
      setAvailableModels(initialAvailableModels);
    }
  }, [initialAvailableModels]);

  // Sync localAvailableModels when prop changes (for "Available Locally" filter)
  useEffect(() => {
    if (initialLocalAvailableModels && initialLocalAvailableModels.length > 0) {
      console.log('[CosyflowPicker] Syncing localAvailableModels from props:', initialLocalAvailableModels.length);
      setLocalAvailableModels(initialLocalAvailableModels);
    }
  }, [initialLocalAvailableModels]);

  // Load default models from IndexedDB for "Recommended" sort
  useEffect(() => {
    const loadDefaultModels = async () => {
      try {
        const models = await db.defaultModels.toArray();
        console.log('[CosyflowPicker] Loaded default models for recommended sort:', models.length);
        setDefaultModels(models);
      } catch (error) {
        console.error('[CosyflowPicker] Error loading default models:', error);
      }
    };
    loadDefaultModels();
  }, []);

  useEffect(() => {
    const savedState = getPersistedState();
    if (!savedState) return;
    setSearchQuery(savedState.searchQuery || '');
    setShowOnlyMine(savedState.showOnlyMine ?? initialShowOnlyMine);
    setSortBy(normalizeSortBy(savedState.sortBy));
    setCostSortAsc(savedState.costSortAsc ?? true);
    setShowFilters(savedState.showFilters || false);
    setFilters(savedState.filters ? { ...defaultFilters, ...savedState.filters } : defaultFilters);
  }, [getPersistedState, initialShowOnlyMine, normalizeSortBy, defaultFilters]);

  // Fetch offline models from cache on mount and poll every 30 seconds
  useEffect(() => {
    const loadOfflineModels = async () => {
      try {
        // Check if any local servers are available and refresh cache
        const { available, servers } = await checkOfflineServersAvailable();
        if (available && Object.keys(servers).length > 0) {
          console.log('[CosyflowPicker] Detected local servers:', servers);
          await refreshModelCache(null, servers);
        }
        const models = await getUnifiedOfflineModels();
        setOfflineModels(models);
        console.log('[CosyflowPicker] Loaded offline models:', models.length);
      } catch (error) {
        console.warn('[CosyflowPicker] Failed to load offline models:', error);
      }
    };

    loadOfflineModels();
    const interval = setInterval(loadOfflineModels, 30000);

    return () => clearInterval(interval);
  }, []);

  // Fetch available models on mount and poll every 60 seconds
  useEffect(() => {
    if (!fetchAvailableModels) return;

    const loadOnlineModels = () => {
      fetchAvailableModels().then(models => {
        if (models && models.length > 0) {
          setAvailableModels(models);
        }
      }).catch(err => console.warn('[CosyflowPicker] Failed to fetch available models:', err));
    };

    loadOnlineModels();
    const interval = setInterval(loadOnlineModels, 60000);

    return () => clearInterval(interval);
  }, [fetchAvailableModels]);

  // Dynamically extract tasks from available cosyflows' tags field
  const availableUseCases = useMemo(() => {
    const useCaseSet = new Set();

    cosyflows.forEach(c => {
      // Primary source: tags from metadata
      if (c.tags && Array.isArray(c.tags)) {
        c.tags.forEach(tag => {
          if (tag && tag.trim()) {
            useCaseSet.add(tag.trim());
          }
        });
      }
    });

    return Array.from(useCaseSet).sort();
  }, [cosyflows]);

  // Dynamically extract unique users/creators from available cosyflows
  const availableUsers = useMemo(() => {
    const userSet = new Set();

    cosyflows.forEach(c => {
      // Get creator/alias name
      const creator = c.creator || c.alias;
      if (creator && creator.trim()) {
        userSet.add(creator.trim());
      }
    });

    return Array.from(userSet).sort();
  }, [cosyflows]);

  // ============================================
  // Effects
  // ============================================

  // Fetch cosyflows on mount only (matching old PWA behavior for fast loading)
  useEffect(() => {
    if (externalCosyflows) {
      setCosyflows(externalCosyflows);
      setIsLoading(false);
      return;
    }

    let isCancelled = false;
    const requestType = showOnlyMine ? 'user' : 'all';

    if (!fetchCosyflows) {
      setIsLoading(false);
      return;
    }

    const loadCosyflows = async () => {
      // Only show loading spinner on initial load, not when switching filters
      if (cosyflows.length === 0) {
        setIsLoading(true);
      }
      console.log(`[CosyflowPicker] Starting fetch for: ${requestType}`);
      try {
        const fetchFn = showOnlyMine && fetchUserCosyflows ? fetchUserCosyflows : fetchCosyflows;
        const data = await fetchFn();

        if (isCancelled) {
          console.log(`[CosyflowPicker] Request cancelled, ignoring stale data for: ${requestType}`);
          return;
        }

        console.log(`[CosyflowPicker] Setting ${data.length} cosyflows for: ${requestType}`);
        setCosyflows(data);
      } catch (error) {
        if (isCancelled) return;
        console.error('Error fetching cosyflows:', error);
        setCosyflows([]);
      } finally {
        if (!isCancelled) {
          setIsLoading(false);
        }
      }
    };

    loadCosyflows();

    return () => {
      isCancelled = true;
    };
  }, [externalCosyflows, fetchCosyflows, fetchUserCosyflows, showOnlyMine, refreshKey]);

  // Sync external loading state
  useEffect(() => {
    if (externalLoading !== undefined) {
      setIsLoading(externalLoading);
    }
  }, [externalLoading]);

  // Fetch available models when Available tab is clicked
  useEffect(() => {
    if (showOnlyAvailable && fetchAvailableModels) {
      fetchAvailableModels().then(models => setAvailableModels(models || []));
    }
  }, [showOnlyAvailable, fetchAvailableModels]);

  // ============================================
  // Filtering & Sorting
  // ============================================

  // Helper to detect generation type from category
  const getGenerationType = useCallback((category) => {
    if (!category) return 'image';
    const lower = category.toLowerCase();
    if (lower.includes('video') || lower.includes('animation')) return 'video';
    if (lower.includes('audio') || lower.includes('music') || lower.includes('sound')) return 'audio';
    if (lower.includes('3d') || lower.includes('mesh') || lower.includes('model')) return '3d';
    return 'image';
  }, []);

  // Helper function to count cosyflows for a given filter state
  const countForFilters = useCallback((testFilters) => {
    let result = cosyflows;

    // Filter out CosyMultiUser
    result = result.filter((c) => !c.title?.includes('CosyMultiUser'));

    // Search filter
    if (searchQuery) {
      const query = searchQuery.toLowerCase();
      result = result.filter(c =>
        c.title.toLowerCase().includes(query) ||
        (c.description && c.description.toLowerCase().includes(query)) ||
        (c.category && c.category.toLowerCase().includes(query))
      );
    }

    // Ownership filter
    if (showOnlyMine && currentUserAccountNo) {
      result = result.filter((c) => c.accountNo === currentUserAccountNo);
    }

    // Available models filter
    if (showOnlyAvailable && availableModels.length > 0) {
      result = result.filter((c) => availableModels.includes(c.title));
    }

    // Properties filter (tri-state)
    if (testFilters.properties && Object.keys(testFilters.properties).length > 0) {
      result = result.filter((c) => {
        for (const [key, requiredValue] of Object.entries(testFilters.properties)) {
          const mustBeTrue = requiredValue === 'true';
          let actualValue;
          switch (key) {
            case 'availableOnline': actualValue = availableModels.includes(c.title); break;
            case 'availableLocally': actualValue = localAvailableModels.includes(c.title); break;
            case 'mine': actualValue = c.accountNo === currentUserAccountNo; break;
            case 'hasWorkflow': actualValue = c.hasComfyWorkflow === true; break;
            case 'trusted': actualValue = c.trusted === true; break;
            case 'public': actualValue = c.isPublic === true; break;
            case 'distributed': actualValue = c.distributed === true; break;
            default: actualValue = false;
          }
          if (mustBeTrue && !actualValue) return false;
          if (!mustBeTrue && actualValue) return false;
        }
        return true;
      });
    }

    // Category filter
    if (testFilters.category !== 'all') {
      result = result.filter((c) => c.category === testFilters.category);
    }

    // Generation type filter
    if (testFilters.generationType && testFilters.generationType !== 'all') {
      result = result.filter((c) => getGenerationType(c.category) === testFilters.generationType);
    }

    // Task filter (matches against tags first, then falls back to category)
    if (testFilters.useCases && testFilters.useCases.length > 0) {
      result = result.filter((c) => {
        // Check tags first
        if (c.tags && Array.isArray(c.tags)) {
          const matchesTags = testFilters.useCases.some(useCase =>
            c.tags.some(tag => tag.toLowerCase() === useCase.toLowerCase())
          );
          if (matchesTags) return true;
        }
        // Fall back to category matching
        const category = (c.category || '').toLowerCase();
        return testFilters.useCases.some(useCase => {
          const ucLower = useCase.toLowerCase();
          return category === ucLower || category.includes(ucLower);
        });
      });
    }

    // Users filter
    if (testFilters.users && testFilters.users.length > 0) {
      result = result.filter((c) => {
        const creator = c.creator || c.alias || '';
        return testFilters.users.includes(creator);
      });
    }

    return result.length;
  }, [cosyflows, searchQuery, showOnlyMine, showOnlyAvailable, availableModels, localAvailableModels, currentUserAccountNo, getGenerationType]);

  // Counts for Output type options
  const outputTypeCounts = useMemo(() => {
    const counts = {};
    GENERATION_TYPE_OPTIONS.forEach(option => {
      counts[option.value] = countForFilters({ ...filters, generationType: option.value });
    });
    return counts;
  }, [filters, countForFilters]);

  // Counts for Property options (shows count for both true and false states)
  const propertyCounts = useMemo(() => {
    const counts = {};
    PROPERTY_OPTIONS.forEach(option => {
      counts[option.value] = {
        yes: countForFilters({ ...filters, properties: { ...filters.properties, [option.value]: 'true' } }),
        no: countForFilters({ ...filters, properties: { ...filters.properties, [option.value]: 'false' } }),
      };
    });
    return counts;
  }, [filters, countForFilters]);

  // Counts for Task options (shows count if only this task is selected)
  const useCaseCounts = useMemo(() => {
    const counts = {};
    availableUseCases.forEach(useCase => {
      counts[useCase] = countForFilters({ ...filters, useCases: [useCase] });
    });
    return counts;
  }, [filters, availableUseCases, countForFilters]);

  // Counts for Users options (shows count if only this user is selected)
  const usersCounts = useMemo(() => {
    const counts = {};
    availableUsers.forEach(user => {
      counts[user] = countForFilters({ ...filters, users: [user] });
    });
    return counts;
  }, [filters, availableUsers, countForFilters]);

  // Base filtered list (before category/useCase/generationType filters)
  // Used to compute available filter options dynamically
  const baseFilteredCosyflows = useMemo(() => {
    let result = cosyflows;

    // Filter out CosyMultiUser cosyflows from all views
    result = result.filter((c) => !c.title?.includes('CosyMultiUser'));

    // Filter untrusted cosyflows unless allowUntrusted is enabled
    // User's own cosyflows and local cosyflows are always shown regardless of trust status
    if (!allowUntrusted) {
      result = result.filter((c) =>
        c.trusted === true ||
        c.accountNo === currentUserAccountNo ||
        localAvailableModels.includes(c.title)
      );
    }

    // Search filter
    if (searchQuery) {
      const query = searchQuery.toLowerCase();
      result = result.filter(
        (c) =>
          c.title.toLowerCase().includes(query) ||
          (c.description && c.description.toLowerCase().includes(query)) ||
          (c.category && c.category.toLowerCase().includes(query))
      );
    }

    // Ownership filter
    if (showOnlyMine && currentUserAccountNo) {
      result = result.filter((c) => c.accountNo === currentUserAccountNo);
    }

    // Available models filter
    if (showOnlyAvailable && availableModels.length > 0) {
      result = result.filter((c) => availableModels.includes(c.title));
    }

    // Online filter
    if (showOnlyOnline && availableModels.length > 0) {
      result = result.filter((c) => availableModels.includes(c.title));
    }

    // Local/Offline filter - use localCosyflows directly instead of filtering online list
    if (showOnlyLocal) {
      if (localCosyflows.length > 0) {
        console.log('[CosyflowPicker] LOCAL filter - using local cosyflows:', localCosyflows.length);
        const localTitles = new Set(localCosyflows.map(c => c.title));
        result = result.filter(c => localTitles.has(c.title));
      } else if (offlineModels.length === 0) {
        console.log('[CosyflowPicker] LOCAL filter - no local server detected');
        result = [];
      } else {
        console.log('[CosyflowPicker] LOCAL filter - no local cosyflows loaded');
        result = [];
      }
    }

    // Category filter
    if (filters.category !== 'all') {
      result = result.filter((c) => c.category === filters.category);
    }

    // Properties filter (tri-state)
    if (filters.properties && Object.keys(filters.properties).length > 0) {
      result = result.filter((c) => {
        for (const [key, requiredValue] of Object.entries(filters.properties)) {
          const mustBeTrue = requiredValue === 'true';
          let actualValue;
          switch (key) {
            case 'availableOnline':
              // Skip filter if online models haven't loaded yet to avoid empty state
              if (availableModels.length === 0) continue;
              actualValue = availableModels.includes(c.title);
              break;
            case 'availableLocally':
              // Skip filter if local models haven't loaded yet to avoid empty state
              if (localAvailableModels.length === 0) continue;
              actualValue = localAvailableModels.includes(c.title);
              break;
            case 'mine': actualValue = c.accountNo === currentUserAccountNo; break;
            case 'hasWorkflow': actualValue = c.hasComfyWorkflow === true; break;
            case 'trusted': actualValue = c.trusted === true; break;
            case 'public': actualValue = c.isPublic === true; break;
            case 'distributed': actualValue = c.distributed === true; break;
            default: actualValue = false;
          }
          if (mustBeTrue && !actualValue) return false;
          if (!mustBeTrue && actualValue) return false;
        }
        return true;
      });
    }

    // Speed filter
    if (filters.minSpeed > 0) {
      result = result.filter((c) => (c.speed || 0) >= filters.minSpeed);
    }

    // Quality filter
    if (filters.minQuality > 0) {
      result = result.filter((c) => (c.quality || 0) >= filters.minQuality);
    }

    // Cost filter
    if (filters.maxCost < 100) {
      result = result.filter((c) => {
        let cost = 0;
        if (getCostDisplay) {
          const costStr = getCostDisplay(c.category);
          cost = costStr ? parseFloat(costStr) : (c.credits_cost || 0);
        } else {
          cost = c.credits_cost || 0;
        }
        return cost <= filters.maxCost;
      });
    }

    return result;
  }, [cosyflows, searchQuery, showOnlyMine, showOnlyAvailable, availableModels, localAvailableModels, currentUserAccountNo, filters.properties, filters.minSpeed, filters.minQuality, filters.maxCost, getCostDisplay, allowUntrusted]);

  // Get unique categories from base filtered list (only shows categories with matches)
  const availableCategories = useMemo(() => {
    const cats = new Set(baseFilteredCosyflows.map(c => c.category).filter(Boolean));
    return ['all', ...Array.from(cats).sort()];
  }, [baseFilteredCosyflows]);

  const filteredCosyflows = useMemo(() => {
    let result = baseFilteredCosyflows;

    // Category filter
    if (filters.category !== 'all') {
      result = result.filter((c) => c.category === filters.category);
    }

    // Generation type filter
    if (filters.generationType && filters.generationType !== 'all') {
      result = result.filter((c) => getGenerationType(c.category) === filters.generationType);
    }

    // Task filter (multi-select - matches against tags first, then category)
    if (filters.useCases && filters.useCases.length > 0) {
      result = result.filter((c) => {
        // Check tags first
        if (c.tags && Array.isArray(c.tags)) {
          const matchesTags = filters.useCases.some(useCase =>
            c.tags.some(tag => tag.toLowerCase() === useCase.toLowerCase())
          );
          if (matchesTags) return true;
        }
        // Fall back to category matching
        const category = (c.category || '').toLowerCase();
        return filters.useCases.some(useCase => {
          const ucLower = useCase.toLowerCase();
          return category === ucLower || category.includes(ucLower);
        });
      });
    } else if (filters.useCase !== 'all') {
      // Legacy single useCase filter support
      result = result.filter((c) => {
        // Check tags first
        if (c.tags && Array.isArray(c.tags)) {
          const matchesTags = c.tags.some(tag => tag.toLowerCase() === filters.useCase.toLowerCase());
          if (matchesTags) return true;
        }
        // Fall back to category matching
        const category = (c.category || '').toLowerCase();
        const useCase = filters.useCase.toLowerCase();
        return category === useCase || category.includes(useCase);
      });
    }

    // Users filter (multi-select - matches against creator/alias)
    if (filters.users && filters.users.length > 0) {
      result = result.filter((c) => {
        const creator = c.creator || c.alias || '';
        return filters.users.includes(creator);
      });
    }

    // Sorting
    switch (sortBy) {
      case 'recommended':
        result = [...result].sort((a, b) => {
          const weightA = defaultModels.find(m => m.model === a.title)?.weighting || 0;
          const weightB = defaultModels.find(m => m.model === b.title)?.weighting || 0;
          return weightB - weightA;
        });
        break;
      case 'alphabetical':
        result = [...result].sort((a, b) => (a.title || '').localeCompare(b.title || ''));
        break;
      case 'popular':
        result = [...result].sort((a, b) => (b.popularity || 0) - (a.popularity || 0));
        break;
      case 'cost':
        result = [...result].sort((a, b) => {
          // Get cost from getCostDisplay (category-based) or fall back to credits_cost
          let costA = 0;
          let costB = 0;

          if (getCostDisplay) {
            const costStrA = getCostDisplay(a.category);
            const costStrB = getCostDisplay(b.category);
            costA = costStrA ? parseFloat(costStrA) : (a.credits_cost || 0);
            costB = costStrB ? parseFloat(costStrB) : (b.credits_cost || 0);
          } else {
            costA = a.credits_cost || 0;
            costB = b.credits_cost || 0;
          }

          return costSortAsc ? costA - costB : costB - costA;
        });
        break;
      case 'speed':
        result = [...result].sort((a, b) => (b.speed || 0) - (a.speed || 0));
        break;
      case 'quality':
        result = [...result].sort((a, b) => (b.quality || 0) - (a.quality || 0));
        break;
      case 'newest':
      default:
        result = [...result].sort((a, b) => {
          const dateA = new Date(a.created_date || 0);
          const dateB = new Date(b.created_date || 0);
          return dateB - dateA;
        });
        break;
    }

    return result;
  }, [cosyflows, searchQuery, showOnlyMine, showOnlyAvailable, showOnlyOnline, showOnlyLocal, availableModels, offlineModels, localCosyflows, localAvailableModels, currentUserAccountNo, fetchUserCosyflows, filters, sortBy, costSortAsc, getCostDisplay, defaultModels]);


  // ============================================
  // Handlers
  // ============================================

  const refreshOfflineModels = useCallback(async () => {
    try {
      const { available, servers } = await checkOfflineServersAvailable();

      if (available && Object.keys(servers).length > 0) {
        console.log('[CosyflowPicker] Refreshing offline models from servers:', servers);
        await refreshModelCache(null, servers);
        const models = await getUnifiedOfflineModels();
        setOfflineModels(models);
        console.log('[CosyflowPicker] Refreshed offline models:', models.length, models);
      } else {
        console.log('[CosyflowPicker] No local servers detected');
        setOfflineModels([]);
      }
    } catch (error) {
      console.warn('[CosyflowPicker] Failed to refresh offline models:', error);
      setOfflineModels([]);
    }
  }, []);

  const fetchLocalCosyflows = useCallback(async () => {
    try {
      const { available, servers } = await checkOfflineServersAvailable();

      setHasLocalServer(available && !!servers.cosy);
      if (available && servers.cosy) {
        console.log('[CosyflowPicker] Fetching local cosyflows from:', servers.cosy);
        const response = await axios.get(`${servers.cosy}/api/generate/get-cosyflows`, { timeout: 5000 });
        const cosyflowsData = response.data.data || response.data;

        if (!Array.isArray(cosyflowsData)) {
          console.warn('[CosyflowPicker] Invalid local cosyflows response');
          setLocalCosyflows([]);
          return;
        }

        const installedCosyflows = cosyflowsData.filter(c => c.install_status === 'INSTALLED');
        console.log('[CosyflowPicker] Found', installedCosyflows.length, 'installed local cosyflows');

        const transformed = installedCosyflows.map(c => {
          let hasComfyWorkflow = false;
          if (c.cosyflow) {
            try {
              const cosyflowDef = typeof c.cosyflow === 'string' ? JSON.parse(c.cosyflow) : c.cosyflow;
              if (cosyflowDef?.prompt && typeof cosyflowDef.prompt === 'object' && Object.keys(cosyflowDef.prompt).length > 0) {
                hasComfyWorkflow = true;
              }
            } catch (e) {
              console.error('[CosyflowPicker] Parse error for:', c.title, e);
            }
          }
          return {
            id: c.uid || c.id || c.title,
            uid: c.uid || c.id,
            title: c.title || c.name,
            description: c.description || '',
            imageUrl: c.image_url || c.thumbnail || c.preview_image,
            category: c.category || 'Local',
            outputType: c.output_type || 'image',
            creator: c.creator || 'Local',
            accountNo: c.account_no,
            hasComfyWorkflow: hasComfyWorkflow,
            isLocal: true,
            serverUrl: servers.cosy,
          };
        });

        setLocalCosyflows(transformed);
        setOfflineModels(transformed.map(c => ({ value: c.title, serverType: 'cosy', serverUrl: servers.cosy })));
        console.log('[CosyflowPicker] Local cosyflows loaded:', transformed.length);
      } else {
        console.log('[CosyflowPicker] No local Cosy server detected');
        setHasLocalServer(false);
        setLocalCosyflows([]);
        setOfflineModels([]);
      }
    } catch (error) {
      console.warn('[CosyflowPicker] Failed to fetch local cosyflows:', error);
      setHasLocalServer(false);
      setLocalCosyflows([]);
      setOfflineModels([]);
    }
  }, []);

  useEffect(() => {
    fetchLocalCosyflows();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [refreshKey]);

  const handleSelect = useCallback((item) => {
    onSelect(item);
  }, [onSelect]);

  const handleBack = useCallback(() => {
    onBack?.();
  }, [onBack]);

  // Wrapper for toggle public that updates local state after parent handler
  const handleTogglePublic = useCallback(async (item, newValue) => {
    if (onTogglePublic) {
      await onTogglePublic(item, newValue);
      // Update local cosyflows state to reflect the change
      setCosyflows(prev => prev.map(c =>
        (c.uid === item.uid || c.id === item.id)
          ? { ...c, isPublic: newValue, public: newValue }
          : c
      ));
    }
  }, [onTogglePublic]);

  // Wrapper for toggle distributed that updates local state after parent handler
  const handleToggleDistributed = useCallback(async (item, newValue) => {
    if (onToggleDistributed) {
      await onToggleDistributed(item, newValue);
      // Update local cosyflows state to reflect the change
      setCosyflows(prev => prev.map(c =>
        (c.uid === item.uid || c.id === item.id)
          ? { ...c, distributed: newValue }
          : c
      ));
    }
  }, [onToggleDistributed]);

  // ============================================
  // Render
  // ============================================

  if (isLoading) {
    return LoadingComponent || <DefaultLoadingSpinner />;
  }

  return (
    <div className={`min-h-screen h-full bg-dark-blue ${classNames.root || ''}`} style={styles.root}>
      {/* Header */}
      {showHeader && (
        <div
          className={`bg-dark-blue/50 border-b border-white/10 ${classNames.header || ''}`}
          style={styles.header}
        >
          <div className="w-full px-4 sm:px-6 lg:px-8 py-6">
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
              <div>
                {onBack && (
                  <button
                    onClick={handleBack}
                    className={`text-white/50 hover:text-white text-base mb-2 inline-flex items-center gap-1 transition-colors ${classNames.backLink || ''}`}
                  >
                    <BackIcon />
                    {backText}
                  </button>
                )}
                <h1 className={`text-3xl sm:text-4xl font-bold text-white ${classNames.headerTitle || ''}`}>
                  {title}
                </h1>
                <p className={`text-lg text-white/60 mt-2 ${classNames.headerSubtitle || ''}`}>
                  {subtitle}
                </p>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Filters */}
      <div
        className={`w-full px-4 sm:px-6 lg:px-8 py-6 ${classNames.filtersContainer || ''}`}
        style={styles.filtersContainer}
      >
        {/* Quick Filter Preset Buttons */}
        <div className="flex flex-wrap gap-2 mb-4">
          {/* Filter Toggle Button - Left */}
          <button
            onClick={() => setShowFilters(!showFilters)}
            className={`flex items-center gap-2 px-4 py-2.5 rounded-lg text-base font-medium transition-colors ${
              showFilters
                ? 'bg-cyan text-dark-blue'
                : 'bg-white/10 text-white hover:bg-white/20'
            }`}
            title={showFilters ? 'Hide filters' : 'Show more filters'}
          >
            {showFilters ? <MinusIcon /> : <PlusIcon />}
            <FilterIcon />
            {/* Show badge if any filters are active */}
            {(filters.generationType !== 'all' ||
              (filters.useCases && filters.useCases.length > 0) ||
              (filters.users && filters.users.length > 0) ||
              (filters.properties && Object.keys(filters.properties).length > 0)) && (
              <span className="bg-yellow text-dark-blue text-xs font-bold px-1.5 py-0.5 rounded-full">
                !
              </span>
            )}
          </button>

          <button
            onClick={() => {
              setSortBy('newest');
              setShowOnlyMine(false);
              setShowOnlyAvailable(false);
              setShowOnlyOnline(false);
              setShowOnlyLocal(false);
              setFilters(defaultFilters);
              setSearchQuery('');
              setShowFilters(false);
            }}
            className={`flex items-center gap-2 px-4 py-2.5 rounded-lg text-base font-medium transition-colors ${!showOnlyMine && !showOnlyAvailable && filters.generationType === 'all' && (!filters.properties || Object.keys(filters.properties).length === 0) && (!filters.useCases || filters.useCases.length === 0) && (!filters.users || filters.users.length === 0) && !searchQuery ? 'bg-yellow text-dark-blue' : 'bg-white/10 text-white hover:bg-white/20'}`}
          >
            <GridIcon />
            All
          </button>
          <button
            onClick={() => {
              setShowOnlyAvailable(true);
              setShowOnlyMine(false);
              setShowOnlyOnline(false);
              setShowOnlyLocal(false);
              setSortBy('newest');
              setFilters({ ...defaultFilters, properties: { availableOnline: 'true' } });
              setSearchQuery('');
              setShowFilters(false);
            }}
            className={`flex items-center gap-2 px-4 py-2.5 rounded-lg text-base font-medium transition-colors ${
              showOnlyAvailable || filters.properties?.availableOnline === 'true'
                ? 'bg-yellow text-dark-blue'
                : 'bg-white/10 text-white hover:bg-white/20'
            }`}
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
            </svg>
            Available Online
          </button>
          <button
            onClick={() => {
              setShowOnlyAvailable(false);
              setShowOnlyMine(false);
              setShowOnlyOnline(false);
              setShowOnlyLocal(true);
              setSortBy('newest');
              setFilters({ ...defaultFilters, properties: { availableLocally: 'true' } });
              setSearchQuery('');
              setShowFilters(false);
            }}
            className={`flex items-center gap-2 px-4 py-2.5 rounded-lg text-base font-medium transition-colors ${
              showOnlyLocal || filters.properties?.availableLocally === 'true'
                ? 'bg-yellow text-dark-blue'
                : 'bg-white/10 text-white hover:bg-white/20'
            }`}
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9.75 17L9 20l-1 1h8l-1-1-.75-3M3 13h18M5 17h14a2 2 0 002-2V5a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
            </svg>
            Available Locally
          </button>
          {showOwnershipFilter && (
            <button
              onClick={() => {
                setShowOnlyMine(false);
                setShowOnlyAvailable(false);
                setShowOnlyOnline(false);
                setShowOnlyLocal(false);
                setSortBy('newest');
                setFilters({ ...defaultFilters, users: currentUserAlias ? [currentUserAlias] : [] });
                setSearchQuery('');
              }}
              className={`flex items-center gap-2 px-4 py-2.5 rounded-lg text-base font-medium transition-colors ${
                filters.users?.length === 1 && filters.users[0] === currentUserAlias
                  ? 'bg-yellow text-dark-blue'
                  : 'bg-white/10 text-white hover:bg-white/20'
              }`}
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
              </svg>
              My Cosyflows
            </button>
          )}
        </div>

        {/* Expanded Filter Panel - directly below preset buttons */}
        {showFilters && (
          <div className="mb-4 p-5 bg-dark-blue/30 border border-cyan/30 rounded-lg">
            {/* Dropdown Filters Row */}
            <div className="flex flex-wrap gap-3 items-center">
              {/* Generation Type Dropdown */}
              <div className="relative">
                <select
                  value={filters.generationType}
                  onChange={(e) => setFilters(prev => ({ ...prev, generationType: e.target.value }))}
                  className={`appearance-none bg-dark-blue/30 border rounded-lg px-4 py-2.5 pr-8 text-white text-base focus:outline-none focus:ring-2 focus:ring-cyan/50 cursor-pointer ${
                    filters.generationType !== 'all' ? 'border-cyan/50' : 'border-white/20'
                  }`}
                >
                  {GENERATION_TYPE_OPTIONS.map(option => (
                    <option key={option.value} value={option.value} className="bg-dark-blue">
                      {option.label} ({outputTypeCounts[option.value] || 0})
                    </option>
                  ))}
                </select>
              </div>

              {/* Multi-Select Use Case Dropdown with Badge */}
              <MultiSelectUseCaseDropdown
                selectedUseCases={filters.useCases}
                onChange={(useCases) => setFilters(prev => ({ ...prev, useCases, useCase: 'all' }))}
                useCases={availableUseCases}
                counts={useCaseCounts}
              />

              {/* Multi-Select Users Dropdown with Badge */}
              <MultiSelectUsersDropdown
                selectedUsers={filters.users || []}
                onChange={(users) => setFilters(prev => ({ ...prev, users }))}
                users={availableUsers}
                counts={usersCounts}
              />

              {/* Properties Tri-State Dropdown */}
              <PropertiesDropdown
                properties={filters.properties || {}}
                onChange={(properties) => setFilters(prev => ({ ...prev, properties }))}
                counts={propertyCounts}
              />

              {/* Reset Filters */}
              <button
                onClick={() => setFilters(defaultFilters)}
                className="px-4 py-2.5 bg-white/10 hover:bg-white/20 text-white/60 rounded-lg text-base transition-colors"
              >
                Reset Filters
              </button>
            </div>
          </div>
        )}

        {/* Search Bar */}
        <div className="relative mb-4">
          <input
            type="text"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            placeholder={searchPlaceholder}
            className="w-full bg-dark-blue/30 border border-white/20 rounded-lg px-4 py-2.5 pl-10 text-white text-base placeholder-white/40 focus:outline-none focus:ring-2 focus:ring-cyan/50 focus:border-cyan/50"
          />
          <svg className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-white/40" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
          </svg>
          {searchQuery && (
            <button
              onClick={() => setSearchQuery('')}
              className="absolute right-3 top-1/2 -translate-y-1/2 text-white/40 hover:text-white"
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          )}
        </div>

        {/* Results count and active filters summary */}
        <div className={`mt-4 flex items-center justify-between ${classNames.resultsCount || ''}`}>
          <div className="text-lg text-white/50">
            {filteredCosyflows.length} cosyflow{filteredCosyflows.length !== 1 ? 's' : ''} found
            {showOnlyMine && ' (showing only yours)'}
            {filters.generationType && filters.generationType !== 'all' && ` • ${filters.generationType.charAt(0).toUpperCase() + filters.generationType.slice(1)}`}
            {filters.useCases && filters.useCases.length > 0 && ` • ${filters.useCases.length} task${filters.useCases.length > 1 ? 's' : ''}`}
            {filters.users && filters.users.length > 0 && ` • ${filters.users.length} user${filters.users.length > 1 ? 's' : ''}`}
            {filters.properties && Object.keys(filters.properties).length > 0 && ` • ${Object.entries(filters.properties).map(([k, v]) => {
              const label = PROPERTY_OPTIONS.find(o => o.value === k)?.label || k;
              return v === 'true' ? label : `Not ${label}`;
            }).join(', ')}`}
          </div>
          <select
            value={sortBy}
            onChange={(e) => setSortBy(e.target.value)}
            className={`appearance-none bg-dark-blue/30 border rounded-lg px-4 py-2 pr-8 text-white text-sm focus:outline-none focus:ring-2 focus:ring-cyan/50 cursor-pointer ${
              sortBy !== 'recommended' ? 'border-cyan/50' : 'border-white/20'
            }`}
          >
            {SORT_OPTIONS.map(option => (
              <option key={option.value} value={option.value} className="bg-dark-blue">
                Sort: {option.label}
              </option>
            ))}
          </select>
        </div>
      </div>


      {/* Filter pending message */}
      {filters.properties?.availableOnline && availableModels.length === 0 && (
        <div className="text-center text-yellow/70 text-sm py-2 px-4">
          <span className="inline-flex items-center gap-2">
            <svg className="w-4 h-4 animate-spin" fill="none" viewBox="0 0 24 24">
              <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
              <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
            </svg>
            Applying "Available Online" filter...
          </span>
        </div>
      )}
      {filters.properties?.availableLocally && localAvailableModels.length === 0 && (
        <div className="text-center text-yellow/70 text-sm py-2 px-4">
          <span className="inline-flex items-center gap-2">
            <svg className="w-4 h-4 animate-spin" fill="none" viewBox="0 0 24 24">
              <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
              <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
            </svg>
            Applying "Available Locally" filter...
          </span>
        </div>
      )}

      {/* Main Grid */}
      <div className="w-full px-4 sm:px-6 lg:px-8 pb-12">
        {filteredCosyflows.length === 0 ? (
          // Empty State
          EmptyComponent || (
            <div className={`text-center py-16 ${classNames.emptyState || ''}`}>
              <div className="text-white/30 mb-4 flex justify-center">
                {showOnlyLocal && offlineModels.length === 0 ? (
                  <svg className="w-16 h-16" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M5 12h14M5 12a2 2 0 01-2-2V6a2 2 0 012-2h14a2 2 0 012 2v4a2 2 0 01-2 2M5 12a2 2 0 00-2 2v4a2 2 0 002 2h14a2 2 0 002-2v-4a2 2 0 00-2-2m-2-4h.01M17 16h.01" />
                  </svg>
                ) : showOnlyMine && !currentUserAccountNo ? (
                  <svg className="w-16 h-16" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                  </svg>
                ) : (
                  <EmptyIcon />
                )}
              </div>
              <h3 className="text-2xl font-semibold text-white mb-2">
                {showOnlyLocal && offlineModels.length === 0
                  ? 'No Local Servers Detected'
                  : showOnlyMine && !currentUserAccountNo
                  ? 'Please Log In'
                  : emptyTitle}
              </h3>
              <p className="text-white/50 text-lg">
                {showOnlyLocal && offlineModels.length === 0
                  ? 'Please start your local server to see your local cosyflows'
                  : showOnlyMine && !currentUserAccountNo
                  ? 'Log in to your account to see your cosyflows'
                  : (showOnlyMine ? emptyOwnedMessage : emptyMessage)}
              </p>
            </div>
          )
        ) : (
          // Grid
          <div
            className={`grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6 ${classNames.grid || ''}`}
            style={styles.grid}
          >
            {filteredCosyflows.map((item) => {
              const isOwned = !!(currentUserAccountNo && item.accountNo === currentUserAccountNo);
              // Try getCostDisplay first, then fall back to item.credits_cost
              const costFromCategory = getCostDisplay ? getCostDisplay(item.category) : null;
              const displayCost = costFromCategory || (item.credits_cost ? String(item.credits_cost) : null);

              // Determine if this cosyflow is available locally (for delete handler)
              const isLocalItem = localAvailableModels.includes(item.title);
              // Enrich item with isLocal flag for handlers
              const enrichedItem = { ...item, isLocal: isLocalItem };

              // Use custom renderer if provided
              if (renderCard) {
                return (
                  <div key={item.id}>
                    {renderCard({
                      item: enrichedItem,
                      isOwned,
                      displayCost,
                      onClick: () => handleSelect(enrichedItem),
                    })}
                  </div>
                );
              }

              // Default card rendering
              return (
                <CosyflowCard
                  key={item.id}
                  item={enrichedItem}
                  isOwned={isOwned}
                  displayCost={displayCost}
                  onClick={handleSelect}
                  onRunInPlayground={onRunInPlayground}
                  onRunOfflineInPlayground={onRunOfflineInPlayground}
                  onRunInCosyUI={onRunInCosyUI}
                  onRunInComfyUI={onRunInComfyUI}
                  onOpenEditor={onOpenEditor}
                  showOpenEditor={(showOnlyMine && isOwned) || isStaffAccount}
                  onCreateTemplate={onCreateTemplate}
                  onInstallLocally={onInstallLocally}
                  isInstalling={isInstalling ? isInstalling(item.uid) : false}
                  onModifyCosyflow={onModifyCosyflow}
                  onDeleteCosyflow={onDeleteCosyflow}
                  onViewDetails={onViewDetails}
                  onCreatorClick={onCreatorClick}
                  onMakeAvailableLocally={onMakeAvailableLocally}
                  onMakeAvailableOnline={onMakeAvailableOnline}
                  onCreateFromDistributed={onCreateFromDistributed}
                  onTogglePublic={handleTogglePublic}
                  onToggleDistributed={handleToggleDistributed}
                  ctaText={ctaText}
                  classNames={cardClassNames}
                  outputTypeConfig={outputTypeConfig}
                  getPlaceholderImage={getPlaceholderImage}
                  renderImage={renderImage}
                  showActionButtons={showActionButtons}
                  showStats={true}
                  availableModels={availableModels}
                  offlineModels={offlineModels}
                  localAvailableModels={localAvailableModels}
                  isStaffAccount={isStaffAccount}
                  isStaffOrCreatorAccount={isStaffOrCreatorAccount}
                  hasLocalServer={hasLocalServer}
                />
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}

export default CosyflowPicker;
